/**
 * Login Modal Component
 * 
 * Modal-based authentication interface that replaces the full-page login flow.
 * Preserves user context by showing login form as an overlay.
 * 
 * Architecture: Uses Layer 2 semantic tokens (--oro-*, --text-*) only.
 * Pattern: Follows existing modal patterns from AdminInviteModal.tsx
 */

import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import Button from './Button';
import Input from './Input';
import Card from './Card';
import { authApi, type ApiError } from '../lib/api';
import { invitesApi } from '../lib/api/invites';
import { useAuth } from '../contexts/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  pendingNavigation?: string | null;
}

export default function LoginModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  pendingNavigation 
}: LoginModalProps) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);
  
  // Get Turnstile site key from environment (optional - graceful degradation)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  const handleAuthSuccess = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    }
    
    // Navigate to pending destination or close modal
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    
    onClose();
  }, [onSuccess, pendingNavigation, navigate, onClose]);

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  // Close modal if user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      handleAuthSuccess();
    }
  }, [isAuthenticated, isOpen, handleAuthSuccess]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setError(null);
      setSuccess(false);
      setTurnstileToken(null);
      setTurnstileError(false);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, handleClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Extract invite code from pendingNavigation if it's an invite URL
      let inviteCode: string | undefined;
      if (pendingNavigation?.startsWith('/invite/')) {
        inviteCode = pendingNavigation.split('/invite/')[1]?.split('?')[0];
      }

      // Pass Turnstile token and invite code if available
      await authApi.requestMagicLink(email, turnstileToken || undefined, inviteCode);
      setSuccess(true);
      // Reset Turnstile widget after successful submission
      setTurnstileToken(null);
    } catch (err) {
      const apiError = err as ApiError;
      
      // Handle bot detection error
      if (apiError.code === 'BOT_CHECK_FAILED') {
        setError('Security check failed. Please refresh the page and try again.');
        setTurnstileError(true);
        setLoading(false);
        return;
      }
      
      // Handle invite-required error - auto-submit to waitlist
      if (apiError.code === 'INVITE_REQUIRED') {
        try {
          await invitesApi.joinWaitlist({
            email,
            source: 'blocked_signup',
          });
          setSuccess(true); // Show same success message
        } catch {
          setError('Unable to join waitlist. Please try again.');
        } finally {
          setLoading(false);
        }
        return;
      }
      
      setError(apiError.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-lg oro-fade-in"
        onClick={handleClose}
        aria-label="Close modal"
      />
      
      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-sm oro-modal-enter">
        <Card className="border-white/5 shadow-xl shadow-black/50 bg-surface-elevated/90">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="text-center flex-1 mr-8">
              <p className="text-sm uppercase tracking-[0.3em] text-text-muted mb-2">Access</p>
              <h2 id="login-modal-title" className="text-h1 font-semibold text-text-primary">
                Login
              </h2>
              <p className="text-text-secondary mt-2">
                Enter your email to receive a secure magic link.
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors rounded-lg hover:bg-oro-surface-alt shrink-0"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Success State */}
          {success && (
            <div className="mb-6 rounded-md border border-green-500/40 bg-green-500/10 p-4">
              <p className="text-sm text-green-100 text-center">
                Check your inbox! If an account exists, we sent a link.
              </p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mb-6 rounded-md border border-red-500/40 bg-red-500/10 p-4">
              <p className="text-sm text-red-200 text-center">
                {error}
              </p>
            </div>
          )}

          {/* Auth Loading State */}
          {authLoading && (
            <div className="mb-6 p-4 text-center">
              <div className="oro-loading h-8 w-8 rounded-full bg-oro-gold mx-auto mb-2"></div>
              <p className="text-text-secondary text-sm">Checking authentication...</p>
            </div>
          )}

          {/* Login Form */}
          {!authLoading && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium tracking-wide text-text-secondary"
                >
                  Email address
                </label>
                <Input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading || success}
                  placeholder="you@example.com"
                  required
                  size="lg"
                />
              </div>
              
              {/* Cloudflare Turnstile Bot Protection */}
              {turnstileSiteKey && !success && (
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setTurnstileError(false);
                    }}
                    onError={() => {
                      setTurnstileError(true);
                      setTurnstileToken(null);
                    }}
                    onExpire={() => {
                      setTurnstileToken(null);
                    }}
                    options={{
                      theme: 'dark',
                      size: 'normal',
                      retry: 'auto',
                      appearance: 'always',
                    }}
                  />
                </div>
              )}
              
              {/* Turnstile Error Message */}
              {turnstileError && (
                <div className="rounded-md border border-oro-border bg-oro-surface-alt p-3">
                  <p className="text-xs text-text-secondary text-center">
                    Security verification failed. Please try refreshing the page.
                  </p>
                </div>
              )}
              
              <Button 
                type="submit" 
                disabled={loading || success}
                loading={loading}
                className="w-full"
                size="lg"
              >
                Send Login Link
              </Button>

              {/* Pending Navigation Indicator */}
              {pendingNavigation && (
                <div className="p-3 bg-oro-surface-alt rounded-lg">
                  <p className="text-xs text-text-muted text-center">
                    After login, you'll be taken to {pendingNavigation}
                  </p>
                </div>
              )}
            </form>
          )}

          {/* Success Actions */}
          {success && !authLoading && (
            <div className="pt-4">
              <Button
                variant="secondary"
                onClick={handleClose}
                className="w-full"
              >
                Continue Browsing
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}